import { TreeIdInput } from '../navigationHelpers/TreeNodeRouter'
import React, { useEffect, useState } from 'react'
import { Box, Button } from '@mui/material'
import {
  useAtom,
  useAtomValue,
  useSetAtom,
} from 'infrastructure/state/jotai.wrapper'
import {
  copySourceNodeAtom,
  copySourceTreeAtom,
  selectedDestinationSiblingNodeAtom,
  selectedNodeAtom,
  workingTreeAtom,
} from './atoms'
import { SourceTree } from './CurriculaTree'
import { useToggleState } from 'shared/hooks'
import { ConfirmDialog } from 'shared/ui'
import { CopyMovePreview } from './CopyMovePreview'
import { useGetTree } from 'shared/hooks'
import { normalizeSourceTree } from './utility'

// This component is responsible for getting the destination tree,
// the parent node and the sibling node to receive the copy or move action.
export function SourceTreeManagement({
  onlySameSource,
  action = 'Select',
}: {
  onlySameSource?: boolean
  action: string
}) {
  const [sourceTree, setSourceTree] = useAtom(copySourceTreeAtom)
  const { tree } = useAtomValue(workingTreeAtom)

  useEffect(() => {
    if (!onlySameSource) return
    if (sourceTree?.tree_id !== tree?.tree_id) {
      setSourceTree(tree)
    }
  }, [onlySameSource, sourceTree, setSourceTree, tree])

  return (
    <Box>
      <GetDestination />
      <CopyMovePreview action={action} />
      <ClearCopySource onlySameSource={!!onlySameSource} />
      <SourceTree action={action} />
    </Box>
  )
}

// This component is responsible for performing the action only after the user confirms
export function DoTheAction({
  actionName,
  action,
  disabled,
}: {
  actionName: string
  action(): Promise<void>
  disabled: boolean
}) {
  const [doingIt, setDoingIt] = useState(false)
  const [confirmIt, setConfirmIt] = useToggleState()

  const doIt = async () => {
    setDoingIt(true)
    setConfirmIt(false)
    await action()
    setDoingIt(false)
  }

  return (
    <Box>
      {doingIt && 'Please wait until operation is finished and tree reloads...'}
      <Button disabled={doingIt || disabled} onClick={() => setConfirmIt()}>
        {actionName}
      </Button>
      <ConfirmDialog
        open={confirmIt}
        title={`${actionName} Node`}
        onClose={() => setConfirmIt(false)}
        onConfirm={doIt}
        canConfirm={!doingIt}
      >
        <ConfirmationBody actionName={actionName} />
      </ConfirmDialog>
    </Box>
  )
}

function ConfirmationBody({ actionName }: { actionName: string }) {
  const { tree: destTree } = useAtomValue(workingTreeAtom)
  const sourceTree = useAtomValue(copySourceTreeAtom)
  const sourceNode = useAtomValue(copySourceNodeAtom)
  const destParent = useAtomValue(selectedNodeAtom)
  const siblingNode = useAtomValue(selectedDestinationSiblingNodeAtom)

  if (!sourceNode || !destParent || !sourceTree || !destTree) {
    return null
  }
  const diffTree = sourceNode.tree_id !== destParent.tree_id
  return (
    <Box>
      <Box paddingBottom="10px">
        {actionName} node with type: <strong>{sourceNode.type}</strong> title:{' '}
        <strong>{sourceNode.title}</strong>
      </Box>
      {diffTree && (
        <Box paddingBottom="10px">
          {actionName} from: {sourceTree.title} Into Tree: {destTree.title}
        </Box>
      )}
      <Box paddingBottom="10px">
        INTO node with type: <strong>{destParent.type}</strong> title:{' '}
        <strong>{destParent.title}</strong>
      </Box>
      <Box>
        {siblingNode &&
          `COMING BEFORE node with type: ${siblingNode.type} title: ${siblingNode.title}`}
        {!siblingNode && 'Coming last in the child list'}
      </Box>
    </Box>
  )
}

function GetDestination() {
  const { tree } = useAtomValue(workingTreeAtom)
  const [sourceTree, setSourceTree] = useAtom(copySourceTreeAtom)
  const [differentTree, setDifferentTree] = useState(false)

  if (sourceTree) return null

  return (
    <Box>
      <Box>
        <Box>Choose Destination Tree</Box>
        <Button
          onClick={() => {
            setSourceTree(tree)
          }}
        >
          Same As Source
        </Button>
        <Button onClick={() => setDifferentTree(true)}>Different Tree</Button>
      </Box>
      {differentTree && <DifferentDestinationTree />}
    </Box>
  )
}

function DifferentDestinationTree() {
  const setTree = useSetAtom(copySourceTreeAtom)
  const getTree = useGetTree()

  const onEnter = async ({
    treeId,
    version,
  }: {
    treeId?: string
    version?: string
  }) => {
    if (!treeId || !version) {
      return
    }
    const tree = await getTree({ treeId, versionId: version })
    tree && setTree(normalizeSourceTree(tree))
  }
  return (
    <Box>
      <TreeIdInput onChange={onEnter} />
    </Box>
  )
}

function ClearCopySource({ onlySameSource }: { onlySameSource: boolean }) {
  const [sourceTree, setSourceTree] = useAtom(copySourceTreeAtom)
  const setSourceNode = useSetAtom(copySourceNodeAtom)
  if (!sourceTree || onlySameSource) return null
  const doIt = () => {
    setSourceTree(undefined)
    setSourceNode(undefined)
  }
  return (
    <Box>
      <Button onClick={doIt}>Change Source Tree</Button>
    </Box>
  )
}
